/* SENTENCES, NOT LABELS — the runtime compiler (Ale ruling 2026-09-02).
 *
 * A SENTENCE explains what is being decided or what the record says; the
 * data points sit INSIDE it as DATA — highlighted, slightly larger — so the
 * officer sees at a glance which values approval writes to the record (on
 * a card) or which values were written (on a read surface).
 *
 * Division of labour, by design:
 *   · the SENTENCE is model-written ONCE per meaning into phrasebook.json
 *     (tools/phrasebook/generate.py, Haiku-class, cents) and committed;
 *   · the DATUM is deterministic — a record/engine value formatted by the
 *     officer formatter (lib/officerFormat) — never produced by a model;
 *   · this module is NON-LLM: it only substitutes and formats.
 *
 * Every template is re-validated here at runtime with the same rules as
 * tools/phrasebook/validate.py (every slot exactly once, no digits or
 * machine tokens outside slots, length bound); a failing entry falls back
 * to its canonical sentence, a missing entry to the caller's fallback.
 * Nothing in this module can emit a raw underscore token or a figure with
 * three or more decimals — the unit tests prove it.
 */

import phrasebookJson from './phrasebook.json' with { type: 'json' }
import {
  cleanEngineText,
  formatNumberString,
  formatOfficerDate,
  formatOfficerValue,
  humanizeTokensIn,
} from '../officerFormat'

export type SlotKind = 'name' | 'text' | 'number' | 'count' | 'date' | 'money' | 'duration' | 'list'

export interface SlotSpec {
  kind: SlotKind
  /** Words used when the value is absent — never a placeholder token. */
  missing?: string
  /** count only: [singular, plural] noun printed after the figure. */
  unit?: [string, string]
  /** A JOINT, not a datum: a word the caller computes from the record
   *  (the article before a legal form — "an AG", "a GmbH") that must
   *  sit in the sentence as plain text, never highlighted. */
  plain?: boolean
  /** A record value the officer must match EXACTLY — a username, a
   *  network address, a clock time — printed as the record holds it:
   *  no humanising, no figure grouping (an address like 10.0.100.200 is
   *  not a decimal), like a document name. Absence still reads in words. */
  verbatim?: boolean
}

export interface PhrasebookEntry {
  meaning: string
  slots: Record<string, SlotSpec>
  template: string
  /** The count slot that chooses a variant (zero / one / plural). */
  count_slot?: string
  variants?: { zero?: string; one?: string; plural?: string }
  canonical: string
}

export interface Phrasebook {
  version: number
  meanings: Record<string, PhrasebookEntry>
}

export type MoneyValue = { amount: string | number | null | undefined; currency?: string | null }
export type SlotValue = string | number | null | undefined | string[] | MoneyValue

export type SentencePart =
  | { kind: 'text'; text: string }
  | { kind: 'datum'; slot: string; datumKind: SlotKind; text: string }
  | { kind: 'missing'; slot: string; text: string }

export type SentenceSource = 'template' | 'zero' | 'one' | 'plural' | 'canonical' | 'fallback'

export interface CompiledSentence {
  meaningId: string
  parts: SentencePart[]
  source: SentenceSource
  /** The plain-text rendering (for titles, tests, logs). */
  text: string
}

export interface CompileOptions {
  /** Used when the meaning is not in the phrasebook at all. */
  fallback?: string
  /** A phrasebook other than the committed one (tests). */
  phrasebook?: Phrasebook
}

/* ── the rules (mirror of tools/phrasebook/validate.py) ────────────────── */

export const MAX_LENGTH = 240
export const SLOT_RE = /«([a-z][a-z0-9_]*)»/g
const DIGIT_RE = /\d/
const MACHINE_TOKEN_RE = /\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/
const LOOSE_DECIMAL_RE = /-?\d[\d,]*\.\d{3,}\b/
const DEFAULT_FALLBACK = 'Details are on the record.'

const DEFAULT_MISSING: Record<SlotKind, string> = {
  name: 'not yet named',
  text: 'not on record',
  number: 'no figure on record',
  count: 'none',
  date: 'no date on record',
  money: 'no amount on record',
  duration: 'no time on record',
  list: 'nothing on record',
}

function slotsIn(text: string): string[] {
  return [...text.matchAll(SLOT_RE)].map((m) => m[1])
}

function outsideSlots(text: string): string {
  return text.replace(SLOT_RE, ' ')
}

/** The checks a single sentence string must pass. `exact` demands every
 *  declared slot exactly once; otherwise a subset is allowed (the zero
 *  variant and the canonical describe absence and may drop slots). */
function checkText(label: string, text: unknown, declared: string[], exact: boolean): string[] {
  const errs: string[] = []
  if (typeof text !== 'string' || text.trim() === '') return [`${label}: empty`]
  if (text.length > MAX_LENGTH) errs.push(`${label}: longer than ${MAX_LENGTH} characters`)
  const used = slotsIn(text)
  for (const s of used) if (!declared.includes(s)) errs.push(`${label}: undeclared slot «${s}»`)
  for (const s of declared) {
    const n = used.filter((u) => u === s).length
    if (n > 1) errs.push(`${label}: slot «${s}» appears ${n} times`)
    if (exact && n === 0) errs.push(`${label}: slot «${s}» missing`)
  }
  const rest = outsideSlots(text)
  if (DIGIT_RE.test(rest)) errs.push(`${label}: digits outside the slots`)
  if (MACHINE_TOKEN_RE.test(rest)) errs.push(`${label}: machine token outside the slots`)
  if (/«|»/.test(rest)) errs.push(`${label}: stray slot marker`)
  return errs
}

/** Validate one phrasebook entry. Empty array = valid. */
export function validateEntry(entry: unknown, id = 'entry'): string[] {
  if (!entry || typeof entry !== 'object') return [`${id}: not an object`]
  const e = entry as Partial<PhrasebookEntry>
  const errs: string[] = []
  if (!e.slots || typeof e.slots !== 'object') return [`${id}: slots missing`]
  const declared = Object.keys(e.slots)
  for (const [name, spec] of Object.entries(e.slots)) {
    if (!spec || typeof spec !== 'object' || !(spec.kind in DEFAULT_MISSING))
      errs.push(`${id}: slot «${name}» has no valid kind`)
    if (spec?.unit && (spec.unit.length !== 2 || spec.kind !== 'count'))
      errs.push(`${id}: slot «${name}» unit must be [singular, plural] on a count slot`)
  }
  errs.push(...checkText(`${id}.template`, e.template, declared, true))
  errs.push(...checkText(`${id}.canonical`, e.canonical, declared, false))
  if (e.variants) {
    if (
      !e.count_slot ||
      !declared.includes(e.count_slot) ||
      e.slots[e.count_slot]?.kind !== 'count'
    )
      errs.push(`${id}: variants need a declared count_slot of kind count`)
    for (const [v, text] of Object.entries(e.variants)) {
      if (!['zero', 'one', 'plural'].includes(v)) errs.push(`${id}: unknown variant ${v}`)
      errs.push(...checkText(`${id}.variants.${v}`, text, declared, v !== 'zero'))
    }
  }
  return errs
}

/** Validate a whole phrasebook — { meaningId: errors[] } for the invalid ones. */
export function validatePhrasebook(book: Phrasebook): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const [id, entry] of Object.entries(book.meanings ?? {})) {
    const errs = validateEntry(entry, id)
    if (errs.length) out[id] = errs
  }
  return out
}

/* ── formatting each datum through the officer formatter ───────────────── */

function isMoney(v: SlotValue): v is MoneyValue {
  return !!v && typeof v === 'object' && !Array.isArray(v) && 'amount' in v
}

function isAbsent(v: SlotValue): boolean {
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  if (isMoney(v))
    return v.amount === null || v.amount === undefined || String(v.amount).trim() === ''
  return false
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

function countOf(v: SlotValue): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && /^-?\d+(?:\.\d+)?$/.test(v.trim())) return Number(v)
  return null
}

/** One value → its officer text. Returns null when the value is absent. */
function formatDatum(kind: SlotKind, v: SlotValue): string | null {
  if (isAbsent(v)) return null
  switch (kind) {
    case 'name':
    case 'text':
      return isFileName(String(v)) ? String(v).trim() : humanizeTokensIn(cleanEngineText(String(v)))
    case 'number':
      return formatOfficerValue(typeof v === 'number' ? String(v) : String(v))
    case 'count': {
      const n = countOf(v)
      return n === null ? formatOfficerValue(String(v)) : formatNumberString(String(Math.trunc(n)))
    }
    case 'date':
      return formatOfficerDate(String(v))
    case 'money': {
      if (isMoney(v)) {
        const amount = formatNumberString(String(v.amount))
        return v.currency ? `${v.currency} ${amount}` : amount
      }
      return formatOfficerValue(String(v))
    }
    case 'duration':
      return humanizeTokensIn(String(v))
    case 'list':
      return joinList((Array.isArray(v) ? v : [String(v)]).map((s) => humanizeTokensIn(String(s))))
  }
}

/** A document name is a record value the officer must find on disk —
 *  never humanised, never split: "register_extract_2026.pdf" stays. */
const FILE_SPLIT_RE = /([A-Za-z0-9][\w.-]*\.(?:pdf|docx?|xlsx?|msg|eml)\b)/
export function isFileName(text: string): boolean {
  return /^[A-Za-z0-9][\w.-]*\.(?:pdf|docx?|xlsx?|msg|eml)$/i.test(text.trim())
}

/** The plain-prose scrub: no machine token, no loose decimal, no ISO date
 *  may survive into the officer's sentence. */
function scrubPlain(text: string): string {
  let t = humanizeTokensIn(text)
  t = t.replace(/-?\d[\d,]*\.\d{3,}\b/g, (m) => formatNumberString(m.replace(/,/g, '')))
  // K7: an ISO date riding inside an engine label ("Namenaktien, 2026-08-05")
  // prints in the house style too.
  t = t.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (m) => formatOfficerDate(m) ?? m)
  return t
}

/** Belt and braces: whatever formatDatum returned, no machine token and no
 *  loose decimal may survive into the officer's sentence — except inside a
 *  document name, which is kept exactly as the record holds it. */
function scrub(text: string): string {
  return text
    .split(FILE_SPLIT_RE)
    .map((chunk, i) => (i % 2 === 1 ? chunk : scrubPlain(chunk)))
    .join('')
}

/* ── compile ───────────────────────────────────────────────────────────── */

function renderTemplate(
  template: string,
  entry: PhrasebookEntry,
  values: Record<string, SlotValue>
): SentencePart[] {
  const parts: SentencePart[] = []
  let last = 0
  for (const m of template.matchAll(SLOT_RE)) {
    const idx = m.index ?? 0
    if (idx > last) parts.push({ kind: 'text', text: template.slice(last, idx) })
    const slot = m[1]
    const spec = entry.slots[slot] ?? { kind: 'text' as SlotKind }
    const raw = values[slot]
    const formatted = spec.verbatim
      ? isAbsent(raw)
        ? null
        : String(raw).trim()
      : formatDatum(spec.kind, raw)
    if (formatted === null) {
      parts.push({ kind: 'missing', slot, text: spec.missing ?? DEFAULT_MISSING[spec.kind] })
    } else if (spec.verbatim) {
      parts.push({ kind: 'datum', slot, datumKind: spec.kind, text: formatted })
    } else if (spec.plain) {
      parts.push({ kind: 'text', text: scrub(formatted) })
    } else {
      parts.push({ kind: 'datum', slot, datumKind: spec.kind, text: scrub(formatted) })
      if (spec.kind === 'count' && spec.unit) {
        const n = countOf(values[slot])
        parts.push({ kind: 'text', text: ` ${n === 1 ? spec.unit[0] : spec.unit[1]}` })
      }
    }
    last = idx + m[0].length
  }
  if (last < template.length) parts.push({ kind: 'text', text: template.slice(last) })
  return parts
}

function chooseVariant(
  entry: PhrasebookEntry,
  values: Record<string, SlotValue>
): { text: string; source: SentenceSource } {
  if (entry.variants && entry.count_slot) {
    const n = countOf(values[entry.count_slot])
    if (n === 0 && entry.variants.zero) return { text: entry.variants.zero, source: 'zero' }
    if (n === 1 && entry.variants.one) return { text: entry.variants.one, source: 'one' }
    if (n !== null && n !== 1 && entry.variants.plural)
      return { text: entry.variants.plural, source: 'plural' }
  }
  return { text: entry.template, source: 'template' }
}

/** Layout hints for the renderer: a datum whose neighbour is punctuation
 *  sits tight against it ("N22.pdf," / "“N22.pdf”"), never boxed apart. */
const PUNCT_AFTER = /^[,.;:!?)\]”’»]/
const PUNCT_BEFORE = /[([“‘«]$/
export function tightBefore(parts: SentencePart[], i: number): boolean {
  const next = parts[i + 1]
  return !!next && next.kind === 'text' && PUNCT_AFTER.test(next.text)
}
export function tightAfter(parts: SentencePart[], i: number): boolean {
  const prev = parts[i - 1]
  return !!prev && prev.kind === 'text' && PUNCT_BEFORE.test(prev.text)
}

export function partsText(parts: SentencePart[]): string {
  return parts.map((p) => p.text).join('')
}

/** The one entry point: a meaning + its record values → sentence parts. */
export function compileSentence(
  meaningId: string,
  values: Record<string, SlotValue> = {},
  opts: CompileOptions = {}
): CompiledSentence {
  const book = opts.phrasebook ?? (phrasebookJson as unknown as Phrasebook)
  const entry = book.meanings?.[meaningId]
  const fallback = (): CompiledSentence => {
    const text = scrub(opts.fallback ?? DEFAULT_FALLBACK)
    return { meaningId, parts: [{ kind: 'text', text }], source: 'fallback', text }
  }
  if (!entry) return fallback()
  if (validateEntry(entry, meaningId).length === 0) {
    const chosen = chooseVariant(entry, values)
    const parts = renderTemplate(chosen.text, entry, values)
    return { meaningId, parts, source: chosen.source, text: partsText(parts) }
  }
  // Invalid entry: the canonical sentence is the safety net — as long as it
  // passes the same rules (a subset of the slots is fine).
  const declared = entry.slots && typeof entry.slots === 'object' ? Object.keys(entry.slots) : []
  if (checkText('canonical', entry.canonical, declared, false).length === 0) {
    const parts = renderTemplate(entry.canonical, { ...entry, slots: entry.slots ?? {} }, values)
    return { meaningId, parts, source: 'canonical', text: partsText(parts) }
  }
  return fallback()
}

/* ── deterministic helpers callers use to build slot values ────────────── */

/** "3 days" / "1 day" / "5 hours" / "12 minutes" / "moments" — the age of a
 *  record timestamp in words. Deterministic for a given `now`. */
export function ageWords(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return null
  const ms = Math.max(0, now - t)
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'moments'
  const hours = Math.floor(ms / 3_600_000)
  if (hours < 1) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
  if (hours < 48) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  const days = Math.floor(hours / 24)
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

/** True when a rendered sentence still carries machine-speak — the scan the
 *  unit tests and the surface specs run. */
export function hasMachineSpeak(text: string): boolean {
  return text
    .split(FILE_SPLIT_RE)
    .some(
      (chunk, i) =>
        i % 2 === 0 &&
        (MACHINE_TOKEN_RE.test(chunk) || LOOSE_DECIMAL_RE.test(chunk) || ISO_DATE_RE.test(chunk))
    )
}
const ISO_DATE_RE = /\b\d{4}-\d{2}-\d{2}\b/

/** An engine reason (a task description, a gate-failure detail) read as a
 *  sentence: machine tokens become words, ISO dates the K7 style, loose
 *  decimals two places — and the record values it names (document names,
 *  dates, figures) become DATA, so the officer sees at a glance what the
 *  reason stands on. The prose stays the engine's; only its values are
 *  lifted. Deterministic, model-free. */
const REASON_DATUM_RE =
  /(\b\d{1,2} (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}\b)|(-?\b\d[\d,]*(?:\.\d{1,2})?\b(?:\s?%)?)/g

export function engineReasonParts(text: string | null | undefined): SentencePart[] {
  if (!text || !text.trim()) return []
  const parts: SentencePart[] = []
  text
    .trim()
    .split(FILE_SPLIT_RE)
    .forEach((chunk, ci) => {
      if (ci % 2 === 1) {
        parts.push({ kind: 'datum', slot: 'reason', datumKind: 'name', text: chunk })
        return
      }
      const clean = scrubPlain(chunk)
      let last = 0
      for (const m of clean.matchAll(REASON_DATUM_RE)) {
        const idx = m.index ?? 0
        // A figure glued to a word (an id like "N22" or "M2") is a name, not a datum.
        const before = clean[idx - 1]
        if (m[2] && before && /[A-Za-z_]/.test(before)) continue
        if (idx > last) parts.push({ kind: 'text', text: clean.slice(last, idx) })
        parts.push({
          kind: 'datum',
          slot: 'reason',
          datumKind: m[1] ? 'date' : 'number',
          text: m[0],
        })
        last = idx + m[0].length
      }
      if (last < clean.length) parts.push({ kind: 'text', text: clean.slice(last) })
    })
  // A document name the engine quoted (“N22.pdf”) is a datum now — the
  // datum's box says what the quotes said; the quotes go.
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]
    if (p.kind !== 'datum' || p.datumKind !== 'name') continue
    const prev = parts[i - 1]
    const next = parts[i + 1]
    if (prev?.kind === 'text' && next?.kind === 'text') {
      const m1 = /[“"‘'«]\s*$/.exec(prev.text)
      const m2 = /^\s*[”"’'»]/.exec(next.text)
      if (m1 && m2) {
        prev.text = prev.text.slice(0, m1.index)
        next.text = next.text.slice(m2[0].length)
      }
    }
  }
  return parts.filter((p) => p.text !== '')
}
